import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api, ProjectFileConfig, ProjectMediaItem, ProjectPriceEstimateConfig, SitePlanData } from '../../services/api';
import BlockIdentificationDialog from './BlockIdentificationDialog';

interface ManageSiteMapDialogProps {
  open: boolean;
  onClose: () => void;
  projects: string[];
  initialProjectName?: string;
  resolveProjectIdByName: (projectName: string) => Promise<string | null>;
  fetchSitePlan: (projectName: string, projectId?: string | null) => Promise<{ sitePlan: SitePlanData | null; projectId: string }>;
  onCoordinatesSaved?: (projectName: string) => void | Promise<void>;
}

function MediaPreview({ item, height = 56 }: { item: ProjectMediaItem; height?: number }) {
  const thumb = item.imageSmall || item.imageMedium || item.imageLarge;
  if (!thumb) return null;
  return (
    <Box
      component="img"
      src={thumb}
      alt="preview"
      sx={{ height, width: 'auto', maxWidth: 80, objectFit: 'cover', borderRadius: 0.5, border: '1px solid rgba(var(--color-text-rgb),0.12)' }}
    />
  );
}

const ManageSiteMapDialog: React.FC<ManageSiteMapDialogProps> = ({
  open,
  onClose,
  projects,
  initialProjectName,
  resolveProjectIdByName,
  fetchSitePlan,
  onCoordinatesSaved,
}) => {
  const [query, setQuery] = useState<string>('');
  const [setupFilter, setSetupFilter] = useState<'all' | 'Y' | 'N'>('all');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [blockDialogOpen, setBlockDialogOpen] = useState<boolean>(false);
  const [projectsWithSetup, setProjectsWithSetup] = useState<Array<{ name: string; hasSetup: boolean }>>([]);

  const [activeTab, setActiveTab] = useState<'sitemap' | 'files' | 'priceEstimate'>('sitemap');

  const [filesProject, setFilesProject] = useState<string>('');
  const [filesProjectId, setFilesProjectId] = useState<string | null>(null);
  const [filesConfig, setFilesConfig] = useState<ProjectFileConfig | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesSaving, setFilesSaving] = useState<'FACTSHEET' | 'BROCHURE' | null>(null);
  const [filesPicker, setFilesPicker] = useState<'FACTSHEET' | 'BROCHURE' | null>(null);
  const [filesSaveError, setFilesSaveError] = useState<string | null>(null);

  const [estProject, setEstProject] = useState<string>('');
  const [estProjectId, setEstProjectId] = useState<string | null>(null);
  const [estConfig, setEstConfig] = useState<ProjectPriceEstimateConfig | null>(null);
  const [estInputs, setEstInputs] = useState<Record<number, string>>({});
  const [estLoading, setEstLoading] = useState(false);
  const [estSavingBedroom, setEstSavingBedroom] = useState<number | null>(null);
  const [estError, setEstError] = useState<string | null>(null);

  const fetchProjectsWithSetup = useCallback(async () => {
    try {
      const data = await api.getProjectsWithSetupStatus();
      setProjectsWithSetup(data);
    } catch {
      setProjectsWithSetup(projects.map((name) => ({ name, hasSetup: false })));
    }
  }, [projects]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSetupFilter('all');
      setSelectedProject('');
      setBlockDialogOpen(false);
      setActiveTab('sitemap');
      setFilesProject('');
      setFilesProjectId(null);
      setFilesConfig(null);
      setFilesPicker(null);
      setEstProject('');
      setEstProjectId(null);
      setEstConfig(null);
      setEstInputs({});
      setEstError(null);
      return;
    }

    const startingProject = (initialProjectName || '').trim();
    if (startingProject) {
      setSelectedProject(startingProject);
      setQuery(startingProject);
    }

    fetchProjectsWithSetup();
  }, [open, initialProjectName, fetchProjectsWithSetup]);

  const displayProjects = projectsWithSetup.length > 0 ? projectsWithSetup : projects.map((name) => ({ name, hasSetup: false }));

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const source = [...displayProjects].sort((a, b) => a.name.localeCompare(b.name));
    let result = normalizedQuery ? source.filter((p) => p.name.toLowerCase().includes(normalizedQuery)) : source;
    if (setupFilter !== 'all') {
      result = result.filter((p) => (setupFilter === 'Y' ? p.hasSetup : !p.hasSetup));
    }
    return result;
  }, [displayProjects, query, setupFilter]);

  const handleEditProject = (projectName: string) => {
    setSelectedProject(projectName);
    setBlockDialogOpen(true);
  };

  const loadFilesConfig = useCallback(async (projId: string) => {
    setFilesLoading(true);
    setFilesConfig(null);
    try {
      const config = await api.getProjectFileConfig(projId);
      setFilesConfig(config);
    } catch {
      setFilesConfig(null);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  const handleFilesProjectChange = useCallback(async (name: string) => {
    setFilesProject(name);
    setFilesConfig(null);
    setFilesPicker(null);
    setFilesSaveError(null);
    if (!name) { setFilesProjectId(null); return; }
    const id = await resolveProjectIdByName(name).catch(() => null);
    setFilesProjectId(id);
    if (id) loadFilesConfig(id);
  }, [resolveProjectIdByName, loadFilesConfig]);

  const handleSelectMedia = useCallback(async (type: 'FACTSHEET' | 'BROCHURE', mediaNumber: number) => {
    if (!filesProjectId) return;
    setFilesSaving(type);
    setFilesSaveError(null);
    try {
      await api.setProjectFileTag(filesProjectId, type, mediaNumber);
      await loadFilesConfig(filesProjectId);
      setFilesPicker(null);
    } catch {
      setFilesSaveError('Failed to save tag. Please try again.');
    } finally {
      setFilesSaving(null);
    }
  }, [filesProjectId, loadFilesConfig]);

  const handleClearTag = useCallback(async (type: 'FACTSHEET' | 'BROCHURE') => {
    if (!filesProjectId) return;
    setFilesSaving(type);
    setFilesSaveError(null);
    try {
      await api.clearProjectFileTag(filesProjectId, type);
      await loadFilesConfig(filesProjectId);
    } catch {
      setFilesSaveError('Failed to clear tag. Please try again.');
    } finally {
      setFilesSaving(null);
    }
  }, [filesProjectId, loadFilesConfig]);

  const loadEstConfig = useCallback(async (projId: string) => {
    setEstLoading(true);
    setEstConfig(null);
    setEstInputs({});
    try {
      const config = await api.getProjectPriceEstimates(projId);
      setEstConfig(config);
      const inputs: Record<number, string> = {};
      config.estimates.forEach((e) => { inputs[e.bedroom] = e.estPrice; });
      setEstInputs(inputs);
    } catch {
      setEstConfig(null);
    } finally {
      setEstLoading(false);
    }
  }, []);

  const handleEstProjectChange = useCallback(async (name: string) => {
    setEstProject(name);
    setEstConfig(null);
    setEstInputs({});
    setEstError(null);
    if (!name) { setEstProjectId(null); return; }
    const id = await resolveProjectIdByName(name).catch(() => null);
    setEstProjectId(id);
    if (id) loadEstConfig(id);
  }, [resolveProjectIdByName, loadEstConfig]);

  const handleSaveEstimate = useCallback(async (bedroom: number) => {
    if (!estProjectId) return;
    const value = (estInputs[bedroom] ?? '').trim();
    if (!value) return;
    setEstSavingBedroom(bedroom);
    setEstError(null);
    try {
      await api.setProjectPriceEstimate(estProjectId, bedroom, value);
      await loadEstConfig(estProjectId);
    } catch {
      setEstError('Failed to save estimate. Please try again.');
    } finally {
      setEstSavingBedroom(null);
    }
  }, [estProjectId, estInputs, loadEstConfig]);

  const handleClearEstimate = useCallback(async (bedroom: number) => {
    if (!estProjectId) return;
    setEstSavingBedroom(bedroom);
    setEstError(null);
    try {
      await api.clearProjectPriceEstimate(estProjectId, bedroom);
      await loadEstConfig(estProjectId);
    } catch {
      setEstError('Failed to clear estimate. Please try again.');
    } finally {
      setEstSavingBedroom(null);
    }
  }, [estProjectId, loadEstConfig]);

  const taggedMedia = (type: 'FACTSHEET' | 'BROCHURE'): ProjectMediaItem | null => {
    if (!filesConfig) return null;
    const tag = type === 'FACTSHEET' ? filesConfig.tags.factsheet : filesConfig.tags.brochure;
    if (!tag) return null;
    return filesConfig.media.find((m) => m.mediaNumber === tag.mediaNumber) ?? null;
  };

  const renderTagSection = (type: 'FACTSHEET' | 'BROCHURE') => {
    const label = type === 'FACTSHEET' ? 'Factsheet' : 'Brochure';
    const tagged = taggedMedia(type);
    const saving = filesSaving === type;
    const pickerOpen = filesPicker === type;
    const tag = type === 'FACTSHEET' ? filesConfig?.tags.factsheet : filesConfig?.tags.brochure;

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-accent)' }}>
            {label}
          </Typography>
          {tag ? (
            <Chip label="Tagged" size="small" color="success" sx={{ fontSize: 10, height: 18 }} />
          ) : (
            <Chip label="Auto-detect" size="small" sx={{ fontSize: 10, height: 18, opacity: 0.6 }} />
          )}
        </Box>

        {tagged ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid rgba(var(--color-text-rgb),0.12)', borderRadius: 1, mb: 1 }}>
            <MediaPreview item={tagged} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{tagged.description || '(no description)'}</Typography>
              <Typography variant="caption" color="text.secondary">{tagged.type || ''} · #{tagged.mediaNumber}</Typography>
            </Box>
            {(tagged.url || tagged.externalLink) && (
              <Tooltip title="View file">
                <IconButton size="small" onClick={() => window.open((tagged.url || tagged.externalLink)!, '_blank')}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
            No tag set — will use auto-detect (LIKE search on Description).
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            onClick={() => setFilesPicker(pickerOpen ? null : type)}
            sx={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)', '&:hover': { borderColor: 'var(--color-accent-strong)' } }}
          >
            {pickerOpen ? 'Hide picker' : (tagged ? 'Change' : 'Set')}
          </Button>
          {tag && (
            <Button
              size="small"
              variant="text"
              disabled={saving}
              onClick={() => handleClearTag(type)}
              sx={{ color: 'rgba(var(--color-text-rgb),0.5)' }}
            >
              {saving ? <CircularProgress size={14} /> : 'Clear tag'}
            </Button>
          )}
        </Box>

        {pickerOpen && filesConfig && (
          <Box sx={{ mt: 1.5, border: '1px solid rgba(var(--color-text-rgb),0.15)', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
            <List dense disablePadding>
              {filesConfig.media.length === 0 && (
                <ListItem><ListItemText secondary="No media found for this project." /></ListItem>
              )}
              {filesConfig.media.map((m, i) => {
                const isSelected = (type === 'FACTSHEET' ? filesConfig.tags.factsheet?.mediaNumber : filesConfig.tags.brochure?.mediaNumber) === m.mediaNumber;
                return (
                  <React.Fragment key={m.mediaNumber}>
                    {i > 0 && <Divider component="li" />}
                    <ListItem
                      sx={{ gap: 1.5, bgcolor: isSelected ? 'rgba(var(--color-accent-rgb),0.08)' : 'transparent' }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {(m.url || m.externalLink) && (
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => window.open((m.url || m.externalLink)!, '_blank')}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            disabled={saving}
                            onClick={() => handleSelectMedia(type, m.mediaNumber)}
                            sx={isSelected ? { bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } } : { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </Box>
                      }
                    >
                      <MediaPreview item={m} height={44} />
                      <ListItemText
                        primary={m.description || '(no description)'}
                        secondary={`${m.type || 'unknown'} · #${m.mediaNumber}`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { maxWidth: 220 } }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            minHeight: '65vh',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage Project</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              mb: 2.5,
              '& .MuiTab-root': { color: 'rgba(var(--color-text-rgb), 0.7)', '&.Mui-selected': { color: 'var(--color-accent)' } },
              '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
            }}
          >
            <Tab label="Manage Sitemap" value="sitemap" />
            <Tab label="Manage Files" value="files" />
            <Tab label="Manage Price Estimate" value="priceEstimate" />
          </Tabs>

          {activeTab === 'sitemap' && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Autocomplete<{ name: string; hasSetup: boolean }>
                options={[...displayProjects].sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(opt) => opt.name}
                value={displayProjects.find((p) => p.name === selectedProject) ?? null}
                onChange={(_, value) => {
                  const next = value?.name ?? '';
                  setSelectedProject(next);
                  setQuery(next);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Project"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="manage-site-map-setup-filter">Setup</InputLabel>
                  <Select
                    labelId="manage-site-map-setup-filter"
                    value={setupFilter}
                    label="Setup"
                    onChange={(e) => setSetupFilter(e.target.value as 'all' | 'Y' | 'N')}
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.9)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--color-text-rgb), 0.23)' } }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Y">Y</MenuItem>
                    <MenuItem value="N">N</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer sx={{ border: '1px solid rgba(var(--color-text-rgb), 0.1)', borderRadius: 1, maxHeight: 420, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.9)' }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.9)', width: 80 }}>Setup</TableCell>
                      <TableCell sx={{ width: 100 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProjects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">No projects found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProjects.map((p) => (
                        <TableRow key={p.name}>
                          <TableCell sx={{ color: 'rgba(var(--color-text-rgb), 0.95)', fontWeight: 500 }}>{p.name}</TableCell>
                          <TableCell sx={{ color: 'rgba(var(--color-text-rgb), 0.95)' }}>{p.hasSetup ? 'Y' : 'N'}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<EditIcon fontSize="small" />}
                              onClick={() => handleEditProject(p.name)}
                              sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {activeTab === 'files' && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Autocomplete<{ name: string; hasSetup: boolean }>
                options={[...displayProjects].sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(opt) => opt.name}
                value={displayProjects.find((p) => p.name === filesProject) ?? null}
                onChange={(_, value) => handleFilesProjectChange(value?.name ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Project"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {!filesProject && (
                <Typography variant="body2" color="text.secondary">Select a project to manage its factsheet and brochure files.</Typography>
              )}

              {filesProject && filesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              )}

              {filesSaveError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>{filesSaveError}</Typography>
              )}

              {filesProject && !filesLoading && filesConfig && (
                <Box>
                  {renderTagSection('FACTSHEET')}
                  <Divider sx={{ my: 1 }} />
                  {renderTagSection('BROCHURE')}
                </Box>
              )}

              {filesProject && !filesLoading && !filesConfig && filesProjectId && (
                <Typography variant="body2" color="text.secondary">No media found for this project.</Typography>
              )}
            </Box>
          )}

          {activeTab === 'priceEstimate' && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Autocomplete<{ name: string; hasSetup: boolean }>
                options={[...displayProjects].sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(opt) => opt.name}
                value={displayProjects.find((p) => p.name === estProject) ?? null}
                onChange={(_, value) => handleEstProjectChange(value?.name ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Project"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {!estProject && (
                <Typography variant="body2" color="text.secondary">
                  Select a project to enter a price estimate (e.g. $1.4xx m) for each bedroom type.
                </Typography>
              )}

              {estProject && estLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              )}

              {estError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>{estError}</Typography>
              )}

              {estProject && !estLoading && estConfig && (
                estConfig.bedrooms.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No bedroom types found for this project.</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    {estConfig.bedrooms.map((br) => {
                      const saved = estConfig.estimates.find((e) => e.bedroom === br)?.estPrice ?? '';
                      const value = estInputs[br] ?? '';
                      const saving = estSavingBedroom === br;
                      const dirty = value.trim() !== saved.trim();
                      return (
                        <Box
                          key={`est-br-${br}`}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid rgba(var(--color-text-rgb),0.12)', borderRadius: 1 }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 56, color: 'var(--color-accent)' }}>
                            {br} BR
                          </Typography>
                          <TextField
                            size="small"
                            placeholder="$1.4xx m"
                            value={value}
                            disabled={saving}
                            onChange={(e) => setEstInputs((prev) => ({ ...prev, [br]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && dirty && value.trim()) handleSaveEstimate(br); }}
                            sx={{ flex: 1 }}
                            inputProps={{ maxLength: 50, autoComplete: 'off' }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disabled={saving || !dirty || !value.trim()}
                            onClick={() => handleSaveEstimate(br)}
                            sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                          >
                            {saving ? <CircularProgress size={14} /> : 'Save'}
                          </Button>
                          {saved && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={saving}
                              onClick={() => handleClearEstimate(br)}
                              sx={{ color: 'rgba(var(--color-text-rgb),0.5)' }}
                            >
                              Clear
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <BlockIdentificationDialog
        open={blockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
        projects={projects}
        initialProjectName={selectedProject}
        resolveProjectIdByName={resolveProjectIdByName}
        fetchSitePlan={fetchSitePlan}
        onCoordinatesSaved={async (projectName) => {
          await Promise.resolve(onCoordinatesSaved?.(projectName));
          await fetchProjectsWithSetup();
        }}
      />
    </>
  );
};

export default ManageSiteMapDialog;
